/*
 * Single source for invoice status + Peppol badge logic, shared by the list,
 * the dashboard and the detail screen so they never diverge.
 * Returns { tone, key } where `key` is an i18n key — callers translate with t().
 * Badge priority (inventory §2.5): draft → credit note → overdue → status map.
 */
const STATUS_MAP = {
  sent: { tone: 'info', key: 'invoices.status.sent' },
  // A received (incoming) invoice that isn't paid yet still needs paying — show it
  // as a yellow "to pay", not a green badge that reads as done/paid.
  received: { tone: 'warning', key: 'invoices.status.received' },
  paid: { tone: 'success', key: 'invoices.status.paid' },
  cancelled: { tone: 'danger', key: 'invoices.status.cancelled' },
  overdue: { tone: 'warning', key: 'invoices.status.overdue' },
  draft: { tone: 'neutral', key: 'invoices.status.draft' },
}

export function invoiceStatus(inv) {
  if (!inv) return { tone: 'neutral', key: 'invoices.status.draft' }
  if (inv.is_draft) return { tone: 'neutral', key: 'invoices.status.draft' }
  if (inv.is_credit_note) return { tone: 'danger', key: 'invoices.status.creditNote' }
  if (inv.is_overdue) return { tone: 'warning', key: 'invoices.status.overdue' }
  return STATUS_MAP[inv.status] || { tone: 'neutral', key: 'invoices.status.draft' }
}

/* Outgoing invoices have a customer; incoming ones a supplier (no explicit field). */
export function isOutgoing(inv) {
  return inv?.customer_id != null
}

const PEPPOL_MAP = {
  TO_SEND: { tone: 'neutral', key: 'invoices.peppol.toSend' },
  SENT: { tone: 'info', key: 'invoices.peppol.sent' },
  OK: { tone: 'success', key: 'invoices.peppol.delivered' },
  RETRY: { tone: 'warning', key: 'invoices.peppol.pending' },
  ERROR: { tone: 'danger', key: 'invoices.peppol.error' },
  ABANDONED: { tone: 'danger', key: 'invoices.peppol.abandoned' },
}

/* Returns a badge descriptor for the Peppol column, or null when N/A. */
export function peppolBadge(inv) {
  if (!inv) return null
  if (isOutgoing(inv) && inv.peppol_status) {
    return { ...(PEPPOL_MAP[inv.peppol_status] || { tone: 'neutral', key: null, raw: inv.peppol_status }), title: inv.peppol_feedback || undefined }
  }
  if (!isOutgoing(inv) && inv.peppol_received_at) {
    return { tone: 'info', key: 'invoices.peppol.received', title: inv.peppol_received_at }
  }
  return null
}
